import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
    Save,
    Eye,
    Rocket,
    Undo2,
    LogOut,
    KeyRound,
    History as HistoryIcon,
    Loader2,
    CheckCircle2,
    AlertCircle,
    FileEdit,
} from "lucide-react";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import { api, formatApiError } from "@/lib/api";
import { SECTION_ORDER, SECTION_SCHEMAS } from "./schemas";
import SchemaEditor from "./SchemaEditor";

const formatTs = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
};

const Banner = ({ kind, text, onClose }) => {
    if (!text) return null;
    const styles = {
        success: "bg-emerald-50 border-emerald-300 text-emerald-800",
        error: "bg-red-50 border-red-300 text-red-700",
        info: "bg-sky-50 border-sky-300 text-sky-800",
    }[kind || "info"];
    const Icon = kind === "error" ? AlertCircle : CheckCircle2;
    return (
        <div className={`flex items-start gap-2 border-l-4 px-3 py-2 text-[13px] ${styles}`} data-testid="admin-banner">
            <Icon className="w-4 h-4 mt-0.5 shrink-0" />
            <span className="flex-1">{text}</span>
            {onClose && (
                <button onClick={onClose} className="text-current opacity-60 hover:opacity-100 text-[12px]">×</button>
            )}
        </div>
    );
};

const SectionList = ({ active, onSelect }) => (
    <ul className="space-y-1" data-testid="admin-section-list">
        {SECTION_ORDER.map((key) => (
            <li key={key}>
                <button
                    type="button"
                    onClick={() => onSelect(key)}
                    data-testid={`admin-nav-${key}`}
                    className={`w-full text-left px-3 py-2 text-[13.5px] flex items-center gap-2 transition-colors ${
                        active === key
                            ? "bg-slate-900 text-teal-300"
                            : "text-slate-200 hover:bg-slate-800"
                    }`}
                >
                    <FileEdit className="w-3.5 h-3.5 opacity-70" />
                    <span className="truncate">{SECTION_SCHEMAS[key].title}</span>
                </button>
            </li>
        ))}
    </ul>
);

// ----- Audit log view -----

const AuditView = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        api.get("/admin/audit-log")
            .then(({ data }) => setItems(data.items || []))
            .finally(() => setLoading(false));
    }, []);
    if (loading) return <div className="text-slate-500 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
    return (
        <div className="bg-white border border-slate-200" data-testid="admin-audit-table">
            <table className="w-full text-[13px]">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                    <tr>
                        <th className="text-left px-4 py-2 font-medium">Time</th>
                        <th className="text-left px-4 py-2 font-medium">Admin</th>
                        <th className="text-left px-4 py-2 font-medium">Action</th>
                        <th className="text-left px-4 py-2 font-medium">Section</th>
                        <th className="text-left px-4 py-2 font-medium">Notes</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 && (
                        <tr><td colSpan={5} className="px-4 py-6 text-slate-400 text-center">No activity yet.</td></tr>
                    )}
                    {items.map((it) => (
                        <tr key={it._id} className="border-t border-slate-100">
                            <td className="px-4 py-2 whitespace-nowrap text-slate-600">{formatTs(it.timestamp)}</td>
                            <td className="px-4 py-2 text-slate-700">{it.admin_email}</td>
                            <td className="px-4 py-2 text-slate-700"><code className="text-[12px] bg-slate-100 px-1.5 py-0.5">{it.action}</code></td>
                            <td className="px-4 py-2 text-slate-600">{it.section || "—"}</td>
                            <td className="px-4 py-2 text-slate-500 truncate max-w-md">{it.summary || ""}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

// ----- Change password -----

const ChangePasswordView = () => {
    const [cur, setCur] = useState("");
    const [next, setNext] = useState("");
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setMsg(null);
        try {
            await api.post("/admin/auth/change-password", { current_password: cur, new_password: next });
            setMsg({ kind: "success", text: "Password updated." });
            setCur(""); setNext("");
        } catch (e2) {
            setMsg({ kind: "error", text: formatApiError(e2) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 max-w-md space-y-4" data-testid="admin-password-form">
            <h3 className="font-display text-lg font-semibold text-slate-900">Change password</h3>
            <label className="block">
                <span className="block text-[12.5px] font-medium text-slate-700 mb-1.5">Current password</span>
                <input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-[13.5px] focus:outline-none focus:border-teal-600" />
            </label>
            <label className="block">
                <span className="block text-[12.5px] font-medium text-slate-700 mb-1.5">New password (min 8 chars)</span>
                <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} className="w-full border border-slate-300 px-3 py-2 text-[13.5px] focus:outline-none focus:border-teal-600" />
            </label>
            {msg && <Banner kind={msg.kind} text={msg.text} onClose={() => setMsg(null)} />}
            <button type="submit" disabled={busy} className="btn-base btn-primary disabled:opacity-60">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                Update password
            </button>
        </form>
    );
};

// ----- Main Dashboard -----

export const AdminDashboard = () => {
    const { user, loading, logout } = useAdminAuth();
    const navigate = useNavigate();

    const [view, setView] = useState("section"); // "section" | "audit" | "password"
    const [activeSection, setActiveSection] = useState(SECTION_ORDER[0]);
    const [draft, setDraft] = useState(null);
    const [draftUpdatedAt, setDraftUpdatedAt] = useState(null);
    const [publishedAt, setPublishedAt] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [publishing, setPublishing] = useState(false);
    const [reverting, setReverting] = useState(false);
    const [msg, setMsg] = useState(null);

    const loadDraft = useCallback(async () => {
        const { data } = await api.get("/admin/content/draft");
        setDraft(data.draft);
        setDraftUpdatedAt(data.draft_updated_at);
        setPublishedAt(data.published_at);
        setDirty(false);
    }, []);

    useEffect(() => {
        if (user) loadDraft().catch((e) => setMsg({ kind: "error", text: formatApiError(e) }));
    }, [user, loadDraft]);

    // Warn on unsaved changes
    useEffect(() => {
        const handler = (e) => {
            if (dirty) {
                e.preventDefault();
                e.returnValue = "";
            }
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [dirty]);

    const onSectionChange = (value) => {
        setDraft((prev) => ({ ...(prev || {}), [activeSection]: value }));
        setDirty(true);
    };

    const save = async () => {
        if (!draft) return;
        setSaving(true);
        setMsg(null);
        try {
            await api.put("/admin/content/draft", { section: activeSection, value: draft[activeSection] });
            setDraftUpdatedAt(new Date().toISOString());
            setDirty(false);
            setMsg({ kind: "success", text: "Draft saved." });
        } catch (e) {
            setMsg({ kind: "error", text: formatApiError(e) });
        } finally {
            setSaving(false);
        }
    };

    const saveAll = async () => {
        if (!draft) return;
        setSaving(true);
        setMsg(null);
        try {
            await api.put("/admin/content/draft", { content: draft });
            setDraftUpdatedAt(new Date().toISOString());
            setDirty(false);
            setMsg({ kind: "success", text: "All sections saved as draft." });
        } catch (e) {
            setMsg({ kind: "error", text: formatApiError(e) });
        } finally {
            setSaving(false);
        }
    };

    const publish = async () => {
        if (dirty) {
            if (!window.confirm("You have unsaved changes. Save them as draft and publish?")) return;
            await saveAll();
        } else if (!window.confirm("Publish the current draft to the live site?")) return;
        setPublishing(true);
        setMsg(null);
        try {
            const { data } = await api.post("/admin/content/publish");
            setPublishedAt(data.published_at);
            setMsg({ kind: "success", text: "Published to the live site." });
        } catch (e) {
            setMsg({ kind: "error", text: formatApiError(e) });
        } finally {
            setPublishing(false);
        }
    };

    const revert = async () => {
        if (!window.confirm("Reset draft AND live content to factory defaults? This cannot be undone.")) return;
        setReverting(true);
        setMsg(null);
        try {
            await api.post("/admin/content/revert");
            await loadDraft();
            setMsg({ kind: "success", text: "Reverted to defaults. Draft and live site reset." });
        } catch (e) {
            setMsg({ kind: "error", text: formatApiError(e) });
        } finally {
            setReverting(false);
        }
    };

    const preview = () => {
        // Open the homepage with a draft preview flag. The PreviewWrapper route
        // mounts ContentProvider in 'draft' mode (uses admin token).
        window.open("/admin/preview", "_blank");
    };

    const schema = useMemo(() => SECTION_SCHEMAS[activeSection], [activeSection]);
    const sectionValue = draft?.[activeSection];

    if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading…</div>;
    if (!user) return <Navigate to="/admin" replace />;

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-slate-100 flex">
            {/* Sidebar */}
            <aside className="w-64 bg-[#0A192F] text-slate-200 flex flex-col">
                <div className="px-5 py-5 border-b border-slate-800">
                    <div className="flex items-center gap-2.5">
                        <span className="inline-flex items-center justify-center w-9 h-9 bg-white text-[#0A192F] font-display font-semibold">A</span>
                        <div className="leading-tight">
                            <p className="font-display font-semibold text-white text-[14px]">Aurea Admin</p>
                            <p className="text-[10.5px] uppercase tracking-[0.18em] text-teal-300 mt-0.5">Content panel</p>
                        </div>
                    </div>
                </div>
                <div className="flex-1 overflow-y-auto py-4 px-3 space-y-4">
                    <div>
                        <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 px-3 mb-2">Content</p>
                        <SectionList active={view === "section" ? activeSection : null} onSelect={(k) => { setView("section"); setActiveSection(k); }} />
                    </div>
                    <div>
                        <p className="text-[10.5px] uppercase tracking-[0.18em] text-slate-500 px-3 mb-2">System</p>
                        <ul className="space-y-1">
                            <li>
                                <button onClick={() => setView("audit")} data-testid="admin-nav-audit" className={`w-full text-left px-3 py-2 text-[13.5px] flex items-center gap-2 ${view === "audit" ? "bg-slate-900 text-teal-300" : "text-slate-200 hover:bg-slate-800"}`}>
                                    <HistoryIcon className="w-3.5 h-3.5 opacity-70" /> Audit log
                                </button>
                            </li>
                            <li>
                                <button onClick={() => setView("password")} data-testid="admin-nav-password" className={`w-full text-left px-3 py-2 text-[13.5px] flex items-center gap-2 ${view === "password" ? "bg-slate-900 text-teal-300" : "text-slate-200 hover:bg-slate-800"}`}>
                                    <KeyRound className="w-3.5 h-3.5 opacity-70" /> Change password
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className="px-3 py-4 border-t border-slate-800">
                    <p className="text-[12px] text-slate-400 px-2 mb-2 truncate">{user?.email}</p>
                    <button onClick={() => { logout(); navigate("/admin", { replace: true }); }} data-testid="admin-logout" className="w-full text-left px-3 py-2 text-[13.5px] text-slate-200 hover:bg-slate-800 flex items-center gap-2">
                        <LogOut className="w-3.5 h-3.5" /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main */}
            <main className="flex-1 overflow-y-auto">
                {/* Action bar */}
                <div className="sticky top-0 z-10 bg-white border-b border-slate-200">
                    <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex flex-col gap-0.5">
                            <p className="font-display text-[15px] font-semibold text-slate-900">
                                {view === "audit" ? "Audit log" : view === "password" ? "Change password" : schema?.title}
                            </p>
                            <p className="text-[11.5px] text-slate-500">
                                Draft saved {formatTs(draftUpdatedAt)} · Last published {formatTs(publishedAt)}
                            </p>
                        </div>
                        {view === "section" && (
                            <div className="flex items-center gap-2 flex-wrap">
                                <button onClick={preview} data-testid="admin-preview" className="btn-base btn-secondary !text-[13px] !py-2 !px-3"><Eye className="w-4 h-4" />Preview</button>
                                <button onClick={save} disabled={saving || !dirty} data-testid="admin-save" className="btn-base !text-[13px] !py-2 !px-3 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Save draft
                                </button>
                                <button onClick={publish} disabled={publishing} data-testid="admin-publish" className="btn-base btn-primary !text-[13px] !py-2 !px-3 disabled:opacity-50">
                                    {publishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}Publish
                                </button>
                                <button onClick={revert} disabled={reverting} data-testid="admin-revert" className="btn-base !text-[13px] !py-2 !px-3 border border-red-200 bg-white text-red-700 hover:bg-red-50 disabled:opacity-50">
                                    {reverting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}Revert
                                </button>
                            </div>
                        )}
                    </div>
                    {msg && <div className="px-6 pb-3"><Banner kind={msg.kind} text={msg.text} onClose={() => setMsg(null)} /></div>}
                    {dirty && view === "section" && (
                        <div className="px-6 pb-3"><Banner kind="info" text="You have unsaved changes." /></div>
                    )}
                </div>

                <div className="p-6 max-w-4xl">
                    {view === "audit" && <AuditView />}
                    {view === "password" && <ChangePasswordView />}
                    {view === "section" && draft && schema && (
                        <SchemaEditor
                            schema={schema}
                            value={sectionValue}
                            onChange={onSectionChange}
                        />
                    )}
                    {view === "section" && !draft && <div className="text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading draft…</div>}
                </div>
            </main>
        </div>
    );
};

export default AdminDashboard;
